import { useActiveMembership, useCan } from '@batiflow/client-core/react';
import { EmptyState, SkeletonList } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Add20Regular, PeopleCommunity24Regular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLink } from '@/shared/components/app-link';
import { useMySpaces } from '../api';
import { useIncomingInvitations } from '../sharing/api';
import { groupSpaces } from '../model';
import { NewSpaceDialog } from '../new-space-dialog';
import { SpaceFrame } from '../space-frame';
import { SpaceRow } from '../space-row';

const useStyles = __css({"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"section":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"heading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fod5ikn","Bg96gwp":"faaz57k","Bhrd7zp":"fl43uef"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r"}});

/** Roles that may open a working group (the project managers only create a project's space). */
const GROUP_ROLES: readonly string[] = ['owner', 'admin'];

/**
 * "Espaces partagés": the spaces of the person's own organisations, then the external spaces they
 * were given access to, each opening /s/{id}. "Nouvel espace partagé" is offered to those who hold
 * `space.create` in the active organisation.
 */
export function SpacesPage() {
  const { t } = useTranslation('spaces');
  const styles = useStyles();
  const navigate = useNavigate();
  const spaces = useMySpaces();
  // What waits for an answer: a failed read only hides the shortcut.
  const waiting = useIncomingInvitations().data?.items.length ?? 0;
  const membership = useActiveMembership();
  const canCreate = useCan('space.create');
  const [creating, setCreating] = useState(false);

  const groups = spaces.data ? groupSpaces(spaces.data.items) : null;
  const orgId = membership?.organizationId ?? null;

  let body;
  if (spaces.isError && !spaces.data) {
    body = (
      <MessageBar intent="error" layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>{t('list.loadFailedTitle')}</MessageBarTitle>
          {t('list.loadFailedBody')}
        </MessageBarBody>
        <MessageBarActions>
          <Button
            onClick={() => {
              void spaces.refetch();
            }}
          >
            {t('common:actions.retry')}
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  } else if (!groups) {
    body = <SkeletonList count={6} />;
  } else {
    body = (
      <>
        <section className={styles.section} aria-labelledby="spaces-own">
          <h2 id="spaces-own" className={styles.heading}>
            {t('list.ownHeading')}
          </h2>
          {groups.own.length === 0 ? (
            <EmptyState
              headingLevel={3}
              illustration={<PeopleCommunity24Regular />}
              title={t('list.ownEmptyTitle')}
              description={t('list.ownEmptyBody')}
              {...(canCreate && orgId
                ? {
                    primaryAction: {
                      label: t('list.newSpace'),
                      onClick: () => {
                        setCreating(true);
                      },
                    },
                  }
                : {})}
            />
          ) : (
            <ul className={styles.list}>
              {groups.own.map((space) => (
                <SpaceRow key={space.id} space={space} />
              ))}
            </ul>
          )}
        </section>
        <section className={styles.section} aria-labelledby="spaces-external">
          <h2 id="spaces-external" className={styles.heading}>
            {t('list.externalHeading')}
          </h2>
          {groups.external.length === 0 ? (
            <EmptyState
              headingLevel={3}
              illustration={<PeopleCommunity24Regular />}
              title={t('list.externalEmptyTitle')}
              description={t('list.externalEmptyBody')}
            />
          ) : (
            <ul className={styles.list}>
              {groups.external.map((space) => (
                <SpaceRow key={space.id} space={space} />
              ))}
            </ul>
          )}
        </section>
      </>
    );
  }

  return (
    <SpaceFrame
      title={t('title')}
      actions={
        canCreate && orgId ? (
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            onClick={() => {
              setCreating(true);
            }}
          >
            {t('list.newSpace')}
          </Button>
        ) : null
      }
    >
      <p className={styles.intro}>{t('intro')}</p>
      {waiting > 0 ? (
        <MessageBar intent="info" layout="multiline">
          <MessageBarBody>
            {t('sharing.incoming.waiting', { count: waiting })}{' '}
            <AppLink inline to="/invitations/spaces">
              {t('sharing.incoming.seeInvitations')}
            </AppLink>
          </MessageBarBody>
        </MessageBar>
      ) : null}
      {body}
      {orgId ? (
        <NewSpaceDialog
          orgId={orgId}
          open={creating}
          canCreateGroup={GROUP_ROLES.includes(membership?.roleKey ?? '')}
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(space) => {
            void navigate({ to: '/s/$spaceId', params: { spaceId: space.id } });
          }}
        />
      ) : null}
    </SpaceFrame>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/pages/spaces-page.griffel.css";